/**
 * Market-only user-facing copy.
 *
 * Phase 1 seed: values mirror the CURRENT market page strings so extraction is
 * behavior-neutral. Later phases update these values (results truthfulness,
 * location-aware grammar, empty/error variants) without touching components.
 *
 * Shared discovery copy (H1, "near you" subtitle) lives in
 * `src/lib/discovery/copy.ts` and is introduced when the market header ships
 * (Phase 2). See buyer-market-discovery-ux § Copy canon.
 */
export const MARKET_COPY = {
  searchPlaceholder: "Search here e.g Smart TV, Microwave, Inverter...",
  sortNearestLabel: "Nearest",
  sortNewestLabel: "Newest",
  /** Results summary fragments (consumed by resultsSummary.ts). */
  resultsNoItemsFound: "No items found",
  /** Intro banner first-paint one-liner + expand affordance (Phase 2, D5). */
  introBannerCollapsed:
    "Browse pre-owned items near you — search or filter to narrow.",
  introBannerTipsLabel: "Tips",
  /** Inline status while location is still resolving for a location-based sort (Phase 2). */
  sortRefiningLocation: "Refining order for your area…",
  /**
   * Catalog cap helper (Phase 3, D3). `{n}` is replaced with the visible count.
   * Shown only on an unfiltered browse that hits the fetch cap.
   */
  capHelper: "Showing {n} items — search or pick a category for more",
  /** Guest helper shown when a signed-out buyer taps Nearest (Phase 3). */
  guestNearestHelper: "Sign in to sort by exact distance",
  /**
   * Explicit pagination control (R3). Replaces infinite scroll with a buyer-
   * triggered "Load more" button for predictable loading.
   */
  loadMoreCta: "Load more",
  loadMoreLoadingLabel: "Loading…",
  /** Active-filter chip row (Phase 4, D4). */
  activeFiltersLabel: "Active filters",
  clearAllFilters: "Clear all filters",
  /** Prefix for a chip's remove-button accessible name, e.g. "Remove Gbagada filter". */
  removeFilterPrefix: "Remove",
  removeFilterSuffix: "filter",
  /** Accessible names for the sort/view icon toggles (Phase 4 a11y). */
  sortNearestAriaLabel: "Sort by nearest",
  sortNewestAriaLabel: "Sort by newest",
  viewGridAriaLabel: "Grid view",
  viewListAriaLabel: "List view",

  /**
   * Empty & error recovery states (Phase 5, C6/C7). Each variant leads with what
   * happened, then a single dominant recovery action. `{q}` is the search query.
   */
  // Error (fetch failed)
  errorTitle: "We couldn't load items",
  errorBody:
    "Something went wrong on our end. Check your connection and try again.",
  errorRetryCta: "Try again",
  errorBrowseAllCta: "Browse all items",
  // Search returned nothing but we have related items to offer
  searchEmptyWithRelatedTitle: 'No exact matches for "{q}"',
  relatedHeading: "Related items you might like",
  // Supplementary shelf shown below actual results on a search
  relatedSuggestionsHeading: "You might also like",
  // Search returned nothing and no related items
  searchEmptyTitle: 'No results for "{q}"',
  // Filters (category/location/price) returned nothing
  filteredEmptyTitle: "No items match your filters",
  filteredEmptyBody: "Try removing a filter or clearing them all to see more.",
  clearFiltersCta: "Clear filters",
  // Nothing to show at all (no filters, empty catalog)
  plainEmptyTitle: "No items found matching your criteria",
  // Shared request-item recovery
  requestItemPrompt: "Can't find what you're looking for?",
  requestItemCta: "Request for an item",
  requestItemHelper:
    "Submit a request and get alerted when a seller has what you need",

  /**
   * Single category surface (Phase 6 + 6.2 nested rail). Category chips stay
   * visible; subcategory chips appear under them after a category is chosen.
   * "Categories" (not "Popular") until a real popularity signal exists.
   */
  categoryQuickPicksHeading: "Categories",
  categoryMoreLabel: "More",
  categoryMoreCloseLabel: "Close",
  categoryRefineHeading: "Refine",
  /** Accessible name for clearing the category filter via the All chip. */
  categoryAllLabel: "All",
} as const;
